import { useCallback, useEffect, useState } from 'react';
import { Bell, Search } from 'lucide-react';
import ListPageHeader from '../common/ListPageHeader.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import { locationHubService } from '../../services/locationHubService.js';
import qrScanNoticeService from '../../services/qrScanNoticeService.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';

const kindLabel = (kind) => (kind === 'crossing' ? 'Crossing' : 'Status update');

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const QrScanNoticesPage = () => {
  const [status, setStatus] = useState('unchecked');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [hubId, setHubId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hubs, setHubs] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    locationHubService.getHubsForSelect()
      .then((response) => {
        const rows = response?.data?.hubs || response?.data?.data || [];
        setHubs(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setHubs([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const checked = status === 'checked' ? true : status === 'unchecked' ? false : undefined;
      const response = await qrScanNoticeService.list(checked, {
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        hub_id: hubId || undefined,
        search: search || undefined,
      });
      setItems(response?.data || []);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not load scan notices');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status, fromDate, toDate, hubId, search]);

  useEffect(() => {
    load();
  }, [load]);

  const checkOne = async (id) => {
    try {
      await qrScanNoticeService.check(id);
      await load();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Could not check this notice');
    }
  };

  const clearFilters = () => {
    setStatus('unchecked');
    setFromDate('');
    setToDate('');
    setHubId('');
    setSearchInput('');
    setSearch('');
  };

  const hasActiveFilters = status !== 'unchecked'
    || Boolean(fromDate)
    || Boolean(toDate)
    || Boolean(hubId)
    || Boolean(search);

  return (
    <DashboardLayout>
      <ListPageHeader title="Scan notices" icon={Bell}>
        <span className="text-sm text-gray-500">{items.length} record{items.length === 1 ? '' : 's'}</span>
      </ListPageHeader>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
        <form
          className="flex flex-col sm:flex-row gap-2 mb-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search shipment, branch, or person"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
            />
          </div>
          <Button type="submit" variant="primary">Search</Button>
        </form>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            >
              <option value="unchecked">Unchecked</option>
              <option value="checked">Checked</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={hubId}
              onChange={(event) => setHubId(event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
            >
              <option value="">All locations</option>
              {sortLocationsAsc(hubs).map((hub) => (
                <option key={hub.id} value={hub.id}>
                  {formatLocationDropdownLabel(hub)}
                </option>
              ))}
            </select>
          </div>
        </ListFilterPanel>
      </div>

      {loading ? (
        <Loader fullScreen={false} message="Loading scan notices" />
      ) : items.length === 0 ? (
        <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">
          No scan notices for these filters.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 w-full min-w-0">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Shipment', 'Scan', 'Scanned at', 'Scanned by', 'Destination', 'Status', 'Action'].map((heading) => (
                    <th
                      key={heading}
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatWhen(item.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{item.shipment_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{kindLabel(item.scan_kind)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.scanned_hub_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{item.scanned_by_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.target_hub_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.is_checked ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {item.is_checked ? 'Checked' : 'Unchecked'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {item.is_checked ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <Button variant="primary" onClick={() => checkOne(item.id)}>
                          Check
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default QrScanNoticesPage;
